import React from "react";
import { MapPinIcon } from "@heroicons/react/24/outline";
import { BookmarkIcon } from "@heroicons/react/24/solid/index.js";

interface ResultQuestionSelectorPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  sectionTitle: string;
  totalQuestions: number;
  answeredQuestions: number[];
  forReviewQuestions: number[];
  onQuestionSelect: (idx: number) => void;
  currentQuestionIdx: number;
}

const ResultQuestionSelectorPopover: React.FC<
  ResultQuestionSelectorPopoverProps
> = ({
  isOpen,
  onClose,
  sectionTitle,
  totalQuestions,
  answeredQuestions,
  forReviewQuestions,
  onQuestionSelect,
  currentQuestionIdx,
}) => {
  if (!isOpen) return null;

  return (
    <div className="absolute left-1/2 -translate-x-1/2 z-50 bottom-full mb-2 w-[500px] max-w-[90vw] bg-white rounded shadow-xl border border-gray-300 p-6 text-[15px] text-black font-sans animate-fade-in">
      <button
        className="absolute top-3 right-3 text-xl text-gray-400 hover:text-gray-700 font-bold cursor-pointer"
        aria-label="Close"
        onClick={onClose}
      >
        ×
      </button>
      <div className="font-bold text-lg text-center mb-2 capitalize">
        {sectionTitle}
      </div>
      <div className="flex items-center justify-center gap-4 mb-2 text-sm">
        <span className="flex items-center gap-1">
          <MapPinIcon className="w-4 h-4" /> Current
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-4 h-4 border border-black border-dashed rounded-none" />{" "}
          Unanswered
        </span>
        <span className="flex items-center gap-1">
          <BookmarkIcon className="w-3 h-3 text-red-500" /> For Review
        </span>
      </div>
      <hr className="my-2" />
      <div className="grid grid-cols-10 gap-2 justify-center mb-4 relative">
        {Array.from({ length: totalQuestions }).map((_, idx) => {
          const isAnswered = answeredQuestions.includes(idx);
          const isForReview = forReviewQuestions.includes(idx);
          return (
            <div key={idx} className="relative">
              {idx === currentQuestionIdx && (
                <div className="absolute left-1/2 -translate-x-1/2 top-[6px] -translate-y-1/2 z-10">
                  <MapPinIcon className="w-4 h-4 text-black" />
                </div>
              )}
              <button
                className={`w-8 h-8 border rounded-none text-base font-bold flex items-center justify-center transition-all cursor-pointer mt-4 relative
                  ${
                    isAnswered
                      ? "bg-[#3451d6] text-white border-[#3451d6] border-solid hover:opacity-80"
                      : "bg-white text-[#3451d6] border-black border-dashed hover:bg-blue-50"
                  }`}
                onClick={() => {
                  onClose();
                  onQuestionSelect(idx);
                }}
              >
                {isForReview && (
                  <div className="absolute -top-2 -right-2 bg-white rounded-full p-0.5">
                    <BookmarkIcon className="w-3 h-3 text-red-500" />
                  </div>
                )}
                <span
                  className={
                    idx === currentQuestionIdx
                      ? isAnswered
                        ? "text-white underline decoration-white decoration-2"
                        : "text-[#3451d6] underline decoration-2"
                      : ""
                  }
                >
                  {idx + 1}
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ResultQuestionSelectorPopover;
